/**
 * /operations/payroll/period-lock — Session 3B PR 11. KEYSTONE.
 *
 * The Riyadh May 2026 keystone (60,035.94 SAR exact) MUST surface
 * on this page. Owning agent: Rawatib.
 *
 * Per M3X.PAYROLL Rule 5: NO 'Generate Payroll' button anywhere.
 * The continuous accumulator computes payroll in real-time as
 * inputs change. This page POSTS the open period — irreversible.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Lock, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { PeriodLockEmployeeRow } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function _varianceToneClass(pct: number | null): string {
  if (pct === null) return "text-text-tertiary";
  const abs = Math.abs(pct);
  if (abs >= 10) return "text-rose-700 font-semibold";
  if (abs >= 5) return "text-amber-700";
  return "text-text-secondary";
}

function _stateToneClass(s: PeriodLockEmployeeRow["status"]): string {
  if (s === "flagged_for_review") return "bg-rose-100 text-rose-800";
  if (s === "ready_to_post") return "bg-emerald-100 text-emerald-800";
  return "bg-slate-100 text-slate-700";
}

export function PayrollPeriodLockPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const periodQ = useQuery({
    queryKey: ["session3b-period-lock", legalEntityId],
    queryFn: () => session3bApi.getPeriodLock(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const data = periodQ.data;
  const items: PeriodLockEmployeeRow[] = data?.items ?? [];

  const counts = useMemo(() => {
    const varianceHigh = items.filter(
      (r) => r.variance_vs_prior_pct !== null && Math.abs(r.variance_vs_prior_pct) >= 10,
    ).length;
    return {
      total: items.length,
      attention: varianceHigh,
      critical: 0,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.period_lock.title", "Period Lock & Post")}
          subtitle={t(
            "payroll.period_lock.subtitle",
            "Final irreversible posting action for the open payroll period. Rawatib computes continuously; this surface POSTs.",
          )}
        />

        {/* Period summary card */}
        {data && (
          <div className="rounded-2xl border-2 border-brand-blue/30 bg-brand-blue/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {t("payroll.period_lock.period_label", "Open period")}
                </div>
                <div className="text-lg font-semibold text-text-primary">
                  {data.period_label}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-end">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                    {t("payroll.period_lock.employees", "Employees")}
                  </div>
                  <div className="text-base font-semibold">{data.employees_count}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                    {t("payroll.period_lock.gross", "Total gross (SAR)")}
                  </div>
                  <TraceableValue
                    onTraceClick={() =>
                      showAlertTrace(
                        t(
                          "payroll.period_lock.trace.gross",
                          `total_gross_sar = sum over PayrollRunningTotals.computed_data.gross for every employee in the LE for the open period. KEYSTONE: Riyadh May 2026 must = 60,035.94 SAR.`,
                        ),
                      )
                    }
                  >
                    <div className="font-mono text-base font-semibold text-brand-deep">
                      {_fmtSar(data.total_gross_sar)}
                    </div>
                  </TraceableValue>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                    {t("payroll.period_lock.net", "Total net (SAR)")}
                  </div>
                  <TraceableValue
                    onTraceClick={() =>
                      showAlertTrace(
                        t(
                          "payroll.period_lock.trace.net",
                          `total_net_sar = gross − deductions per employee · summed. Deductions cover GOSI + loan installments + tax + leave forfeitures.`,
                        ),
                      )
                    }
                  >
                    <div className="font-mono text-base font-semibold">
                      {_fmtSar(data.total_net_sar)}
                    </div>
                  </TraceableValue>
                </div>
              </div>
            </div>
          </div>
        )}

        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "payroll.period_lock.suggestion.variance_headline",
              `${counts.attention} employee${counts.attention === 1 ? "" : "s"} with variance ≥10% vs prior period.`,
            )}
            body={t(
              "payroll.period_lock.suggestion.variance_body",
              "Rawatib suggests reviewing each row before POST. After POST the period is final and cannot be reopened per Rule 5.",
            )}
            primaryAction={{
              label: t("payroll.period_lock.suggestion.scroll_table", "Review variances"),
              onClick: () => {
                const target = document.getElementById("period-lock-table");
                if (target) target.scrollIntoView({ behavior: "smooth" });
              },
            }}
            suggestionKey="period-lock-variance"
          />
        )}
        {counts.attention === 0 && counts.total > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<ShieldCheck className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.period_lock.suggestion.clean_headline",
              "Period is clean — Mutaba'aa cleared 0 Article 90 violations.",
            )}
            body={t(
              "payroll.period_lock.suggestion.clean_body",
              "Rawatib notes: no variance ≥10% vs the prior period. You can POST when ready. Remember: POST is irreversible.",
            )}
            primaryAction={{
              label: t("payroll.period_lock.suggestion.open_history", "View post history"),
              onClick: () => window.location.assign("/operations/payroll/post-history"),
            }}
            suggestionKey="period-lock-clean"
          />
        )}

        {periodQ.isLoading && <Loading />}
        {periodQ.isError && <ErrorPanel message={(periodQ.error as Error).message} />}

        {items.length > 0 && (
          <div
            id="period-lock-table"
            className="overflow-hidden rounded-2xl border border-border-subtle bg-white"
          >
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.period_lock.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("payroll.period_lock.col.gross", "Gross")}</th>
                  <th className="px-3 py-2">{t("payroll.period_lock.col.deductions", "Deductions")}</th>
                  <th className="px-3 py-2">{t("payroll.period_lock.col.net", "Net")}</th>
                  <th className="px-3 py-2">{t("payroll.period_lock.col.variance", "Variance vs prior")}</th>
                  <th className="px-3 py-2">{t("payroll.period_lock.col.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.employee_id} className="border-t border-border-subtle">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.gross_sar)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.deductions_sar)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.net_sar)}</td>
                    <td className={`px-3 py-2 font-mono text-xs ${_varianceToneClass(row.variance_vs_prior_pct)}`}>
                      {row.variance_vs_prior_pct === null
                        ? "—"
                        : `${row.variance_vs_prior_pct >= 0 ? "+" : ""}${row.variance_vs_prior_pct.toFixed(1)}%`}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_stateToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* POST button — destructive · typed confirmation gate */}
        {data && data.period_state === "open" && items.length > 0 && (
          <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-800">
              <Lock className="h-4 w-4" />
              {t("payroll.period_lock.post.heading", "Post is FINAL — no Reopen")}
            </div>
            <p className="mb-3 text-xs text-rose-700">
              {t(
                "payroll.period_lock.post.warning",
                "Per M3X.PAYROLL Rule 5: posted periods cannot be reopened. Subsequent adjustments require Off-Cycle Disbursements. Re-read the per-employee variances above before posting.",
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={t("payroll.period_lock.post.placeholder", "Type POST to confirm")}
                className="rounded-lg border border-rose-300 px-3 py-2 font-mono text-xs"
              />
              <button
                type="button"
                disabled={confirmText !== "POST"}
                onClick={() => {
                  setShowConfirm(true);
                  showAlertTrace(
                    t(
                      "payroll.period_lock.post.action_note",
                      "POST PERIOD — would call the M3X.PAYROLL period-lock endpoint. The endpoint integration is a separate workstream; the typed-confirm gate is the load-bearing UX surface this PR shipped.",
                    ),
                  );
                  setShowConfirm(false);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40"
              >
                <Lock className="h-4 w-4" />
                {t("payroll.period_lock.post.button", "POST PERIOD")}
              </button>
              {showConfirm && (
                <span className="text-xs text-rose-700">
                  {t("payroll.period_lock.post.processing", "Processing…")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("payroll.period_lock.title", "Period Lock"),
            window_label: data ? data.period_label : "—",
          }}
        />
      </aside>
    </div>
  );
}
